/**
 * GASTIFY — AstroPay Service
 * ---------------------------
 * Helper para integración con AstroPay OneTouch Deposit API.
 *
 * MODELO DE COBRO: B2B SaaS — Gastify cobra suscripciones a las orgs
 * con un ÚNICO set de credenciales globales (no per-org).
 *
 * Las credenciales viven en variables de entorno del proyecto Supabase:
 *   ASTROPAY_API_URL              (sandbox: https://onetouch-api-sandbox.astropay.com)
 *                                 (prod:    https://onetouch-api.astropay.com)
 *   ASTROPAY_GATEWAY_API_KEY      (Merchant-Gateway-Api-Key header)
 *   ASTROPAY_GATEWAY_SECRET_KEY   (HMAC-SHA256 signing key)
 *   ASTROPAY_POS_EXTERNAL_ID      (opcional, según endpoint)
 *
 * Cada pago se identifica por org_id en:
 *   - merchant_deposit_id: "gastify-{org_id}-{timestamp}"
 *   - user.merchant_user_id: org_id
 *   - subscriptions.organization_id
 *
 * Deploy edge functions:
 *   supabase functions deploy astropay-create-payment
 *   supabase functions deploy astropay-webhook
 *
 * NOTA: las funciones fetchOrgCredentials/saveOrgCredentials/hasOrgCredentials
 * de más abajo están DEPRECATED. Quedaron para histórico y por si en el
 * futuro se habilita un modelo multi-merchant (cada org cobra a sus clientes).
 */

import { supabase } from '../supabase';

// ── Config ──────────────────────────────────────────────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const DEFAULT_SUBSCRIPTION_PROVIDER = 'mercadopago';

export const SUBSCRIPTION_PAYMENT_PROVIDERS = Object.freeze({
  astropay: {
    id: 'astropay',
    label: 'AstroPay',
    functionName: 'astropay-create-payment',
    credentialsError: 'AstroPay credentials not configured (missing env vars)',
    missingConfigMessage:
      'Faltan las variables globales de AstroPay en Supabase (ASTROPAY_GATEWAY_API_KEY, ASTROPAY_GATEWAY_SECRET_KEY, ASTROPAY_API_URL).',
  },
  mercadopago: {
    id: 'mercadopago',
    label: 'Mercado Pago',
    functionName: 'mp-create-subscription-payment',
    credentialsError: 'Mercado Pago credentials not configured (missing env vars)',
    missingConfigMessage:
      'Faltan las variables globales de Mercado Pago en Supabase (MERCADOPAGO_ACCESS_TOKEN y, recomendado, MERCADOPAGO_WEBHOOK_SECRET/PUBLIC_APP_URL).',
  },
});

export function normalizeSubscriptionPaymentProvider(provider) {
  const normalized = String(provider || '')
    .trim()
    .toLowerCase();

  return normalized === 'mercadopago' ? 'mercadopago' : DEFAULT_SUBSCRIPTION_PROVIDER;
}

export function getSubscriptionPaymentProviderMeta(provider) {
  const normalizedProvider = normalizeSubscriptionPaymentProvider(provider);
  return SUBSCRIPTION_PAYMENT_PROVIDERS[normalizedProvider];
}

export async function fetchSubscriptionBillingConfig() {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('subscription_payment_provider')
      .eq('id', 'global')
      .maybeSingle();

    if (error) throw error;

    const provider = normalizeSubscriptionPaymentProvider(
      data?.subscription_payment_provider,
    );

    return {
      subscription_payment_provider: provider,
      provider,
      providerMeta: getSubscriptionPaymentProviderMeta(provider),
    };
  } catch (err) {
    console.warn('[Subscriptions] fetchSubscriptionBillingConfig fallback:', err?.message);
    const provider = DEFAULT_SUBSCRIPTION_PROVIDER;
    return {
      subscription_payment_provider: provider,
      provider,
      providerMeta: getSubscriptionPaymentProviderMeta(provider),
    };
  }
}

export async function reconcileMercadoPagoSubscriptionReturn(params = {}) {
  const resp = await fetch(`${SUPABASE_URL}/functions/v1/mp-reconcile-subscription-return`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
    },
    body: JSON.stringify(params),
  });

  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(payload?.error || `Edge function error: ${resp.status}`);
  }

  return payload;
}

// ── Pricing legacy (per-branch/per-user) — RETIRADO ─────────────────────
// El modelo activo de cobro son los planes fijos en subscription_plans
// (basic/premium/enterprise con price_monthly). Las funciones
// fetchGlobalSubscriptionPricing, calculateEffectiveMonthlyPrice,
// fetchEffectiveMonthlyPrices y fetchEffectiveMonthlyPrice fueron eliminadas
// porque ningún call-site las usa. La tabla subscription_pricing puede
// permanecer en la DB para histórico — el código frontend no la consulta más.

export async function fetchEffectiveMonthlyPrice(orgId) {
  return null;
}

export function addOneBillingMonth(dateLike = new Date()) {
  const sourceDate = dateLike instanceof Date ? dateLike : new Date(dateLike);
  const nextDate = new Date(sourceDate);
  const sourceDay = nextDate.getDate();

  nextDate.setDate(1);
  nextDate.setMonth(nextDate.getMonth() + 1);

  const lastDayOfTargetMonth = new Date(
    nextDate.getFullYear(),
    nextDate.getMonth() + 1,
    0,
  ).getDate();

  nextDate.setDate(Math.min(sourceDay, lastDayOfTargetMonth));
  return nextDate;
}

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Fetch all active plans from subscription_plans table.
 * Falls back to hardcoded defaults if DB is unreachable.
 */
export async function fetchPlans() {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('[AstroPay] fetchPlans fallback:', err?.message);
    return getHardcodedPlans();
  }
}

/**
 * Fetch a single plan by its key.
 */
export async function fetchPlanByKey(planKey) {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('key', planKey)
      .eq('is_active', true)
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.warn('[AstroPay] fetchPlanByKey fallback:', err?.message);
    const plans = getHardcodedPlans();
    return plans.find((p) => p.key === planKey) || null;
  }
}

/**
 * Fetch subscriptions for an organization (history).
 */
export async function fetchSubscriptions(orgId) {
  if (!orgId) return [];
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('[AstroPay] fetchSubscriptions error:', err?.message);
    return [];
  }
}

/**
 * Create a payment link for a subscription.
 *
 * Llama al edge function astropay-create-payment, que usa las
 * credenciales globales de Gastify (env vars) para generar un link
 * de pago. El org_id viaja en merchant_deposit_id y merchant_user_id
 * para poder asociar el pago a la organización cuando llega el webhook.
 */
export async function createPaymentLink({ orgId, planKey, planName, amount, currency = null }) {
  if (!orgId) throw new Error('[Subscriptions] orgId es requerido para crear un link de pago');

  // Anchor del primer período: la fecha más tardía entre hoy, el fin del trial
  // y la fecha actual de vencimiento de suscripción. Esto evita que el usuario
  // pierda los días de trial restantes si decide pagar antes.
  const now = new Date();
  let anchor = now;
  try {
    const { data: orgRow } = await supabase
      .from('organizations')
      .select('trial_ends_at, subscription_expires_at')
      .eq('id', orgId)
      .maybeSingle();
    const trialEnds = orgRow?.trial_ends_at ? new Date(orgRow.trial_ends_at) : null;
    const subExpires = orgRow?.subscription_expires_at ? new Date(orgRow.subscription_expires_at) : null;
    if (trialEnds && trialEnds > anchor) anchor = trialEnds;
    if (subExpires && subExpires > anchor) anchor = subExpires;
  } catch (anchorErr) {
    console.warn('[Subscriptions] No se pudo leer trial_ends_at, uso fecha actual.', anchorErr);
  }

  const periodStart = anchor.toISOString().split('T')[0];
  const nextMonth = addOneBillingMonth(anchor);
  const periodEnd = nextMonth.toISOString().split('T')[0];

  try {
    // Prioridad de pricing (modelo fijo por plan):
    //   1. `amount` explícito recibido del caller (ej: SuscripcionStatus, PagoRequerido)
    //   2. subscription_plans.price_monthly (fuente real de billing)
    //   3. subscription_pricing.amount_monthly (legacy por sucursal/usuario) — solo como red de seguridad
    const planDetails =
      amount == null && planKey
        ? await fetchPlanByKey(planKey).catch(() => null)
        : null;
    const effectivePricing =
      amount == null && !Number(planDetails?.price_monthly)
        ? await fetchEffectiveMonthlyPrice(orgId).catch(() => null)
        : null;
    const billingConfig = await fetchSubscriptionBillingConfig();
    const providerMeta = billingConfig.providerMeta;
    const finalAmount = Number(
      amount
      ?? planDetails?.price_monthly
      ?? effectivePricing?.amount_monthly
      ?? 0
    );
    const finalCurrency =
      currency
      || planDetails?.currency
      || effectivePricing?.currency
      || 'ARS';

    if (!(Number(finalAmount) > 0)) {
      throw new Error('No se pudo determinar el monto mensual de la suscripcion.');
    }

    const resp = await fetch(`${SUPABASE_URL}/functions/v1/${providerMeta.functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
      body: JSON.stringify({
        organization_id: orgId,
        plan_key: planKey,
        plan_name: planName,
        amount: finalAmount,
        currency: finalCurrency,
        period_start: periodStart,
        period_end: periodEnd,
      }),
    });

    if (!resp.ok) {
      const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
      if (resp.status === 422) {
        console.warn(`[Subscriptions] ${providerMeta.missingConfigMessage}`);
        throw new Error(errBody.error || providerMeta.credentialsError);
      }
      throw new Error(`Edge function error: ${resp.status} ${JSON.stringify(errBody)}`);
    }

    const result = await resp.json();
    console.info(
      `[Subscriptions] Payment link created for org ${orgId} with ${providerMeta.label}`,
    );
    return {
      ...result,
      provider: result.provider || providerMeta.id,
      provider_label: result.provider_label || providerMeta.label,
    };
  } catch (err) {
    console.error('[Subscriptions] createPaymentLink error:', err);
    throw err;
  }
}

/**
 * Manually activate a subscription (superadmin action or webhook callback).
 */
export async function activateSubscription(orgId, subscriptionId, planKey) {
  const { error } = await supabase.rpc('activate_subscription', {
    p_org_id: orgId,
    p_subscription_id: subscriptionId,
    p_plan_key: planKey,
  });

  if (error) throw error;
  return true;
}

/**
 * Change an organization's plan. Creates a new pending subscription.
 */
export async function changePlan({ orgId, newPlanKey }) {
  const plan = await fetchPlanByKey(newPlanKey);
  if (!plan) throw new Error(`Plan "${newPlanKey}" no encontrado`);

  // Update the org's plan key
  const { error: updateErr } = await supabase
    .from('organizations')
    .update({
      current_plan_key: newPlanKey,
      custom_modules: plan.modules,
      updated_at: new Date().toISOString(),
    })
    .eq('id', orgId);

  if (updateErr) throw updateErr;

  // Create payment link for the new plan
  const result = await createPaymentLink({
    orgId,
    planKey: plan.key,
    planName: plan.name,
  });

  return { plan, ...result };
}

/**
 * Cancel a pending subscription.
 */
export async function cancelSubscription(subscriptionId) {
  const { error } = await supabase
    .from('subscriptions')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', subscriptionId);

  if (error) throw error;
  return true;
}

// ── Credential Management (DEPRECATED) ─────────────────────────────────
// Las funciones de abajo manejan credenciales POR ORGANIZACIÓN en la
// tabla org_payment_credentials. En el modelo actual (B2B SaaS, Gastify
// cobra suscripciones con credenciales globales) NO se usan.
// Se conservan por compatibilidad con datos históricos y por si en el
// futuro se habilita modelo multi-merchant. Si vas a llamarlas, primero
// confirmá que es el caso correcto — sino estás introduciendo un bug.
// ───────────────────────────────────────────────────────────────────────

/**
 * @deprecated En modelo B2B SaaS las credenciales son globales (env vars).
 * Esta función queda solo para lectura de datos legacy / superadmin.
 */
export async function fetchOrgCredentials(orgId) {
  if (!orgId) return null;
  try {
    const { data, error } = await supabase
      .from('org_payment_credentials')
      .select('id, organization_id, provider, api_url, pos_external_id, environment, is_active, notes, created_at, updated_at')
      .eq('organization_id', orgId)
      .eq('provider', 'astropay')
      .single();

    if (error) return null;
    return data;
  } catch (err) {
    console.warn('[AstroPay] fetchOrgCredentials error:', err?.message);
    return null;
  }
}

/**
 * @deprecated En modelo B2B SaaS las credenciales son globales (env vars).
 * No usar para nuevas integraciones. Lanzá un error si alguien la llama
 * por accidente para evitar dejar credenciales huérfanas en la tabla.
 */
export async function saveOrgCredentials({ orgId, gatewayApiKey, posExternalId, apiUrl, environment = 'sandbox', notes = '' }) {
  if (typeof window !== 'undefined') {
    console.warn('[AstroPay] saveOrgCredentials está deprecated. Gastify usa credenciales globales. Si necesitás multi-merchant, hablar con producto antes de habilitarlo.');
  }
  if (!orgId || !gatewayApiKey || !posExternalId) {
    throw new Error('orgId, gatewayApiKey y posExternalId son requeridos');
  }

  const payload = {
    organization_id: orgId,
    provider: 'astropay',
    gateway_api_key: gatewayApiKey,
    pos_external_id: posExternalId,
    api_url: apiUrl || (environment === 'production'
      ? 'https://onetouch-api.astropay.com'
      : 'https://onetouch-api-sandbox.astropay.com'),
    environment,
    is_active: true,
    notes,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('org_payment_credentials')
    .upsert(payload, { onConflict: 'organization_id,provider' })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

/**
 * @deprecated En modelo B2B SaaS las credenciales son globales (env vars),
 * no se chequean por org. Esta función siempre debería responder false
 * en el flujo nuevo. Mantenida para no romper código legacy.
 */
export async function hasOrgCredentials(orgId) {
  if (!orgId) return false;
  const { count, error } = await supabase
    .from('org_payment_credentials')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('provider', 'astropay')
    .eq('is_active', true);

  return !error && (count || 0) > 0;
}

// ── Hardcoded fallback plans ────────────────────────────────────────────
function getHardcodedPlans() {
  return [
    {
      key: 'basic',
      name: 'Plan Arranque',
      description: 'Ventas, caja, stock y clientes en una sola rutina.',
      price_monthly: 50000,
      currency: 'ARS',
      features: [
        'Punto de venta ágil',
        'Stock en tiempo real',
        'Clientes y gastos',
        'Hasta 500 operaciones/mes',
      ],
      operation_limit: 500,
      sort_order: 1,
    },
    {
      key: 'premium',
      name: 'Plan Control',
      description: 'Compras, facturación y tesorería para crecer con método.',
      price_monthly: 80000,
      currency: 'ARS',
      features: [
        'Todo del Plan Arranque',
        'Compras y proveedores',
        'Facturación integrada',
        'Tesorería operativa',
        'Hasta 2500 operaciones/mes',
      ],
      operation_limit: 2500,
      sort_order: 2,
    },
    {
      key: 'enterprise',
      name: 'Plan Dirección',
      description: 'Producción, flujo de fondos y rentabilidad de punta a punta.',
      price_monthly: 120000,
      currency: 'ARS',
      features: [
        'Todo del Plan Control',
        'Producción y recetas',
        'Flujo de fondos proyectado',
        'Control de rentabilidad',
        'Operaciones ilimitadas',
      ],
      operation_limit: 0,
      sort_order: 3,
    },
  ];
}

/**
 * Format price in ARS currency.
 */
export function formatPrice(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}
